import { Badge, BlockStack, Button, Card, EmptyState, IndexTable, InlineStack, Modal, Page, Tag, Text, TextField, Toast } from "@shopify/polaris";
import React, { useCallback, useState, useEffect } from "react";
import RichTextEditor from "../components/RichTextEditor";
import { useAppFormik } from "../contexts/FormikContext";
import { useDispatch, useSelector } from "react-redux";
import { createSettingAsync, getAllSetting, getSettingAsync, getActivityLogsAsync } from "../redux/slice/commonSlice";

const ActivityLogPage = () => {
    const { values, setFieldValue } = useAppFormik();
    const dispatch = useDispatch();
    const { isCreateSettingLoading, activityLogs: activityLogsState, isActivityLogsLoading } = useSelector(getAllSetting);
    const [selectedLogs, setSelectedLogs] = useState([]);
    const [toastActive, setToastActive] = useState(false);
    const [toastMessage, setToastMessage] = useState("");
    const [messageModalActive, setMessageModalActive] = useState(false);
    const [ipAddressModalActive, setIpAddressModalActive] = useState(false);
    const [ipAddressInput, setIpAddressInput] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [limit] = useState(10);

    // Get values from Formik - check both blockIp and blocks for blocked IPs
    const blockedIpAddresses = values.blockIp?.blockedIpAddresses?.length > 0 
        ? values.blockIp.blockedIpAddresses 
        : (values.blocks?.blockedIpAddresses || []);
    const blockingMessage = values.blockIp?.blockingMessage || "";
    
    // Sync blocks.blockedIpAddresses with blockIp.blockedIpAddresses if they differ
    useEffect(() => {
        if (values.blockIp?.blockedIpAddresses?.length > 0 && 
            JSON.stringify(values.blockIp.blockedIpAddresses) !== JSON.stringify(values.blocks?.blockedIpAddresses || [])) {
            setFieldValue("blocks.blockedIpAddresses", values.blockIp.blockedIpAddresses);
        } else if (values.blocks?.blockedIpAddresses?.length > 0 && 
                   JSON.stringify(values.blocks.blockedIpAddresses) !== JSON.stringify(values.blockIp?.blockedIpAddresses || [])) {
            setFieldValue("blockIp.blockedIpAddresses", values.blocks.blockedIpAddresses);
        }
    }, [values.blockIp?.blockedIpAddresses, values.blocks?.blockedIpAddresses, setFieldValue]);

    // Fetch activity logs on component mount and when page changes
    useEffect(() => {
        dispatch(getActivityLogsAsync({ page: currentPage, limit }));
    }, [dispatch, currentPage, limit]);

    // Format date from ISO string to readable format
    const formatDate = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Map API data to table format
    const activityLogs = (activityLogsState?.data || []).map((log) => ({
        id: log._id,
        action: log.label || log.action,
        ipAddress: log.ipAddress,
        location: log.location,
        date: formatDate(log.date || log.createdAt),
        isBlocked: blockedIpAddresses.includes(log.ipAddress)
    }));

    const handleDeleteLogs = () => {
        if (selectedLogs.length > 0) {
            showToast(`${selectedLogs.length} log(s) deleted successfully.`);
            setSelectedLogs([]);
        }
    };

    const handleAddIpAddress = () => {
        const trimmedIp = ipAddressInput.trim();
        if (trimmedIp && !blockedIpAddresses.includes(trimmedIp)) {
            const updatedIps = [...blockedIpAddresses, trimmedIp];
            // Update both blockIp and blocks
            setFieldValue("blockIp.blockedIpAddresses", updatedIps);
            setFieldValue("blocks.blockedIpAddresses", updatedIps);
            setIpAddressInput("");
        }
    };

    const handleRemoveIpAddress = async (ip) => {
        const updatedIps = blockedIpAddresses.filter((address) => address !== ip);
        // Update both blockIp and blocks
        setFieldValue("blockIp.blockedIpAddresses", updatedIps);
        setFieldValue("blocks.blockedIpAddresses", updatedIps);
        
        // Save to backend - sync to both blockIp and blocks
        const updatedValues = {
            ...values,
            blockIp: {
                ...values.blockIp,
                blockedIpAddresses: updatedIps
            },
            blocks: {
                ...values.blocks,
                blockedIpAddresses: updatedIps
            }
        };
        
        await dispatch(createSettingAsync({ 
            payload: { settings: updatedValues }, 
            callback: () => {
                dispatch(getSettingAsync({}));
                showToast(`IP address ${ip} removed`);
            }
        }));
    };

    const handleSaveMessage = async () => {
        await dispatch(createSettingAsync({ 
            payload: { settings: values }, 
            callback: () => {
                dispatch(getSettingAsync({}));
                showToast("Blocking message saved successfully!");
                setMessageModalActive(false);
            }
        }));
    };

    const handleSaveIpAddresses = async () => {
        // Ensure both blockIp and blocks are synced before saving
        const updatedValues = {
            ...values,
            blocks: {
                ...values.blocks,
                blockedIpAddresses: blockedIpAddresses
            }
        };
        
        await dispatch(createSettingAsync({ 
            payload: { settings: updatedValues }, 
            callback: () => {
                dispatch(getSettingAsync({}));
                showToast(`${blockedIpAddresses.length} IP address(es) saved successfully!`);
                setIpAddressModalActive(false);
                setIpAddressInput("");
            }
        }));
    };

    const handleToggleBlock = async (logId, ipAddress) => {
        if (!ipAddress) return;
        
        const isCurrentlyBlocked = blockedIpAddresses.includes(ipAddress);
        let updatedIps;
        
        if (isCurrentlyBlocked) {
            // Unblock - remove from blocked list
            updatedIps = blockedIpAddresses.filter((ip) => ip !== ipAddress);
        } else {
            // Block - add to blocked list
            if (!blockedIpAddresses.includes(ipAddress)) {
                updatedIps = [...blockedIpAddresses, ipAddress];
            } else {
                return; // Already blocked
            }
        }
        
        // Update Formik state for both blockIp and blocks
        setFieldValue("blockIp.blockedIpAddresses", updatedIps);
        setFieldValue("blocks.blockedIpAddresses", updatedIps);
        
        // Save to backend - sync to both blockIp and blocks
        const updatedValues = {
            ...values,
            blockIp: {
                ...values.blockIp,
                blockedIpAddresses: updatedIps
            },
            blocks: {
                ...values.blocks,
                blockedIpAddresses: updatedIps
            }
        };
        
        await dispatch(createSettingAsync({ 
            payload: { settings: updatedValues }, 
            callback: () => {
                dispatch(getSettingAsync({}));
                showToast(`IP address ${ipAddress} ${isCurrentlyBlocked ? 'unblocked' : 'blocked'}`);
            }
        }));
    };

    const showToast = useCallback((message) => {
        setToastMessage(message);
        setToastActive(true);
    }, []);

    const toastMarkup = toastActive ? (
        <Toast content={toastMessage} onDismiss={() => setToastActive(false)} />
    ) : null;
    return (
        <Page
            title="Activity log"
            primaryAction={{
                content: "Add Message",
                onAction: () => setMessageModalActive(true)
            }}
            secondaryActions={[
                {
                    content: "Add IP Address",
                    onAction: () => setIpAddressModalActive(true)
                }
            ]}
        >
            <Card roundedAbove="sm" padding='200'>
                <InlineStack align="space-between" blockAlign="center">
                    <Text variant="headingMd" as="h2">
                        Activity Logs
                    </Text>
                    {selectedLogs.length > 0 && (
                        <Button tone="critical" onClick={handleDeleteLogs}>
                            Delete Selected ({selectedLogs.length.toString()})
                        </Button>
                    )}
                </InlineStack>
                <br />
                {activityLogs.length > 0 ? (
                    <IndexTable
                        selectable={false}
                        resourceName={{
                            singular: "log",
                            plural: "logs",
                        }}
                        itemCount={activityLogsState?.total || activityLogs.length}
                        selectedItemsCount={
                            selectedLogs.length === activityLogs.length ? "All" : selectedLogs.length
                        }
                        onSelectionChange={(selectionType, toggleType, selection) => {
                            if (selectionType === "all") {
                                setSelectedLogs(
                                    selectedLogs.length === activityLogs.length
                                        ? []
                                        : activityLogs.map((log) => log.id)
                                );
                            } else if (typeof selection === "string") {
                                setSelectedLogs((prev) =>
                                    prev.includes(selection)
                                        ? prev.filter((id) => id !== selection)
                                        : [...prev, selection]
                                );
                            }
                        }}
                        headings={[
                            { title: "ID" },
                            { title: "Action" },
                            { title: "IP Address" },
                            { title: "Location" },
                            { title: "Date" },
                            { title: "Status" },
                            { title: "Actions" },
                        ]}
                        pagination={{
                            hasNext: activityLogsState?.totalPages > currentPage,
                            hasPrevious: currentPage > 1,
                            onNext: () => {
                                if (activityLogsState?.totalPages > currentPage) {
                                    setCurrentPage(currentPage + 1);
                                }
                            },
                            onPrevious: () => {
                                if (currentPage > 1) {
                                    setCurrentPage(currentPage - 1);
                                }
                            },
                        }}
                        loading={isActivityLogsLoading}
                    >
                        {activityLogs.map((log, index) => (
                            <IndexTable.Row
                                id={log.id}
                                key={log.id}
                                selected={selectedLogs.includes(log.id)}
                                position={index}
                            >
                                <IndexTable.Cell>{index + 1}</IndexTable.Cell>
                                <IndexTable.Cell>{log.action}</IndexTable.Cell>
                                <IndexTable.Cell>{log.ipAddress}</IndexTable.Cell>
                                <IndexTable.Cell>{log.location}</IndexTable.Cell>
                                <IndexTable.Cell>{log.date}</IndexTable.Cell>
                                <IndexTable.Cell>
                                    <Badge tone={blockedIpAddresses.includes(log.ipAddress) ? "critical" : "success"}>
                                        {blockedIpAddresses.includes(log.ipAddress) ? "Blocked" : "Active"}
                                    </Badge>
                                </IndexTable.Cell>
                                <IndexTable.Cell>
                                    <Button
                                        size="micro"
                                        tone={blockedIpAddresses.includes(log.ipAddress) ? "success" : "critical"}
                                        onClick={() => handleToggleBlock(log.id, log.ipAddress)}
                                    >
                                        {blockedIpAddresses.includes(log.ipAddress) ? "Unblock" : "Block"}
                                    </Button>
                                </IndexTable.Cell>
                            </IndexTable.Row>
                        ))}
                    </IndexTable>
                ) : (
                    <EmptyState
                        heading="No Activity Yet"
                        image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                    >
                        <p>No suspicious activity detected yet.</p>
                    </EmptyState>
                )}
            </Card>
            {toastMarkup}

            {/* Add Message Modal */}
            <Modal
                open={messageModalActive}
                onClose={() => setMessageModalActive(false)}
                title="Add Blocking Message"
                primaryAction={{
                    content: "Save",
                    onAction: handleSaveMessage,
                    loading: isCreateSettingLoading,
                    disabled: isCreateSettingLoading,
                }}
                secondaryActions={[
                    {
                        content: "Cancel",
                        onAction: () => setMessageModalActive(false),
                    },
                ]}
            >
                <Modal.Section>
                    <BlockStack gap="400">
                        <Text tone="subdued" variant="bodySm">
                            Create a custom message that will be displayed to users when they are blocked from accessing your store.
                        </Text>
                        <RichTextEditor
                            label="Blocking Message"
                            value={blockingMessage}
                            onChange={(val) => setFieldValue("blockIp.blockingMessage", val)}
                        />
                    </BlockStack>
                </Modal.Section>
            </Modal>

            {/* Add IP Address Modal */}
            <Modal
                open={ipAddressModalActive}
                onClose={() => {
                    setIpAddressModalActive(false);
                    setIpAddressInput("");
                }}
                title="Add IP Address"
                primaryAction={{
                    content: "Save",
                    onAction: handleSaveIpAddresses,
                    loading: isCreateSettingLoading,
                    disabled: isCreateSettingLoading,
                }}
                secondaryActions={[
                    {
                        content: "Cancel",
                        onAction: () => {
                            setIpAddressModalActive(false);
                            setIpAddressInput("");
                        },
                    },
                ]}
            >
                <Modal.Section>
                    <BlockStack gap="400">
                        <Text tone="subdued" variant="bodySm">
                            Add IP addresses to block from accessing your store. You can add multiple IP addresses. Users from these IPs will be blocked and shown the blocking message.
                        </Text>
                        <InlineStack gap="200" align="start" blockAlign="center">
                            <TextField
                                label="IP Address"
                                value={ipAddressInput}
                                onChange={setIpAddressInput}
                                placeholder="e.g., 192.168.1.1"
                                autoComplete="off"
                                helpText="Enter a valid IP address (IPv4 format: xxx.xxx.xxx.xxx)"
                            />
                            <Button onClick={handleAddIpAddress} disabled={!ipAddressInput.trim()}>
                                Add
                            </Button>
                        </InlineStack>
                        {blockedIpAddresses.length > 0 && (
                            <BlockStack gap="200">
                                <Text variant="bodyMd" fontWeight="semibold">
                                    Blocked IP Addresses ({blockedIpAddresses.length}):
                                </Text>
                                <Text tone="subdued" variant="bodySm">
                                    Click the X icon on any tag to remove an IP address from the blocked list.
                                </Text>
                                <InlineStack gap="200" wrap>
                                    {blockedIpAddresses.map((ip) => (
                                        <Tag key={ip} onRemove={() => handleRemoveIpAddress(ip)}>
                                            {ip}
                                        </Tag>
                                    ))}
                                </InlineStack>
                            </BlockStack>
                        )}
                    </BlockStack>
                </Modal.Section>
            </Modal>
        </Page>
    )
};

export default ActivityLogPage;